import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@koob/dates';
import { sanitizeRichText } from '@koob/ui';

export default function ChatboxMessage({
  message,
  displayAs,
  dangerouslyHtml,
  currentOrganizationId,
}) {
  const { t } = useTranslation('booking');

  const currentDateTimeZone = formatDateTime(
    message.createdAt,
    'DD/MM/YYYY HH:mm:ss',
  );

  // A message is "mine" when it comes from my organization. On multi-DMC trips
  // this distinguishes two DMCs that share the same scope. When the sender
  // organization is unknown (e.g. booking chat), fall back to scope comparison.
  const isOwnMessage =
    message.organization?.id != null && currentOrganizationId != null
      ? String(message.organization.id) === String(currentOrganizationId)
      : message.senderType === displayAs;

  const linkify = text => {
    const urlRegex = /(?:https?:\/\/)?(?:[\w-]+\.)+[\w-]+(?:\/[^\s]*)?/gi;

    const linkColor = isOwnMessage ? 'text-white' : 'text-orange-500';

    return text.split('\n').map((line, index) => {
      const parts = [];
      let lastIndex = 0;

      line.replace(urlRegex, (match, offset) => {
        if (offset > lastIndex) {
          parts.push(line.slice(lastIndex, offset));
        }

        const href = match.startsWith('http') ? match : `https://${match}`;

        parts.push(
          <a key={offset} href={href} className={`underline ${linkColor}`}>
            {match}
          </a>,
        );

        lastIndex = offset + match.length;
      });

      if (lastIndex < line.length) {
        parts.push(line.slice(lastIndex));
      }

      return (
        <span key={index}>
          {parts}
          <br />
        </span>
      );
    });
  };

  return message.senderType !== 'system' ? (
    <div
      className={[
        'p-4 border rounded-lg',
        isOwnMessage
          ? 'bg-orange-500 text-orange-50 ml-[25%]'
          : 'bg-gray-100 text-gray-700 mr-[25%]',
      ].join(' ')}
    >
      <div className="flex justify-between items-start gap-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
          <div className="text-base font-semibold break-words">
            {message.user
              ? `${message?.user?.firstName} ${message?.user?.lastName}`
              : t('chat.hotelKeeper')}
          </div>

          {message.organization?.displayName && (
            <div className="text-sm opacity-80 break-words">
              {message.organization.displayName}
            </div>
          )}

          <div className="border border-black/20 px-1 rounded shrink-0">
            <div className="text-sm font-base uppercase">
              {message.senderType}
            </div>
          </div>
        </div>

        <div className="whitespace-nowrap shrink-0">{currentDateTimeZone}</div>
      </div>

      <div className="whitespace-pre-line" data-testid="message">
        {dangerouslyHtml ? (
          <div
            className={`col-span-2 prose prose-sm ${
              isOwnMessage ? 'text-white' : 'text-gray-600'
            } text-lg`}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(message.message),
            }}
          />
        ) : (
          linkify(message.message)
        )}
      </div>
    </div>
  ) : (
    <div className="text-center text-gray-500 whitespace-pre-wrap ">
      {message.message}
    </div>
  );
}
